import { createSlice } from "@reduxjs/toolkit";
import { loginApi, } from "apis/eduidLogin";
import { ToUs } from "helperFunctions/ToUs";
// Define the initial state using that type. Export for use as a baseline in tests.
export const initialState = {
    tou: { available_versions: Object.keys(ToUs) },
    authn_options: {},
};
export const loginSlice = createSlice({
    name: "login",
    initialState,
    reducers: {
        addLoginRef: (state, action) => {
            // Add the login reference (currently an UUID extracted from the URL), to the store.
            state.ref = action.payload.ref;
            state.start_url = action.payload.start_url;
        },
        addThisDevice: (state, action) => {
            // Add the identifier for this device from local storage.
            state.this_device = action.payload;
        },
        clearThisDevice: (state) => {
            if (state.this_device !== undefined) {
                // Move contents from this_device to previous_this_device
                state.previous_this_device = state.this_device;
            }
            state.this_device = undefined;
        },
        setRememberMe: (state, action) => {
            // Set the 'remember me' user preference.
            state.remember_me = action.payload;
        },
        startLoginWithAnotherDevice: (state, action) => {
            if (action.payload.username && !state.authn_options.forced_username) {
                state.authn_options.forced_username = action.payload.username;
            }
            state.next_page = "OTHER_DEVICE";
        },
        stopLoginWithAnotherDevice: (state) => {
            // Abort/cancel/recover from use another device state
            state.next_page = undefined;
            state.other_device1 = undefined;
        },
        callLoginNext: (state) => {
            // Trigger the Login component fetching of next action to perform from the backend.
            state.next_page = undefined;
        },
    },
    extraReducers: (builder) => {
        builder
            .addMatcher(loginApi.endpoints.fetchUseOtherDevice1.matchFulfilled, (state, action) => {
            // Store the result for the user requesting to use another device to log in.
            if (action.payload.payload.state === "ABORTED" || action.payload.payload.state === "FINISHED") {
                // Remove state in frontend too when backend confirms the request has been aborted or finished
                state.other_device1 = undefined;
                state.next_page = undefined;
            }
            else {
                state.other_device1 = action.payload.payload;
            }
        })
            .addMatcher(loginApi.endpoints.fetchUseOtherDevice1.matchRejected, (state) => {
            state.other_device1 = undefined;
        })
            .addMatcher(loginApi.endpoints.fetchUseOtherDevice2.matchFulfilled, (state, action) => {
            // Store the result from fetching state about logging in on another device (from this device).
            state.other_device2 = action.payload.payload;
        })
            .addMatcher(loginApi.endpoints.fetchUseOtherDevice2.matchRejected, (state) => {
            state.other_device2 = undefined;
        })
            .addMatcher(loginApi.endpoints.fetchAbort.matchFulfilled, (state, action) => {
            if (action.payload.payload.finished) {
                // Trigger fetching of /next on successful abort
                state.next_page = undefined;
            }
        })
            .addMatcher(loginApi.endpoints.fetchNext.matchPending, (state) => {
            state.fetching_next = true;
        })
            .addMatcher(loginApi.endpoints.fetchNext.matchFulfilled, (state, action) => {
            // Store the result from asking the backend what action to perform next
            const samlParameters = action.payload.payload.action === "FINISHED" ? action.payload.payload.parameters : undefined;
            state.next_page = action.payload.payload.action;
            state.post_to = action.payload.payload.target;
            state.saml_parameters = samlParameters;
            if (action.payload.payload.authn_options)
                state.authn_options = action.payload.payload.authn_options;
            state.fetching_next = false;
            state.service_info = action.payload.payload.service_info;
            state.assurance = action.payload.payload.assurance;
            state.error = undefined;
        })
            .addMatcher(loginApi.endpoints.fetchNext.matchRejected, (state, action) => {
            var _a;
            if (action.payload.error &&
                ((_a = action.payload.payload) === null || _a === void 0 ? void 0 : _a.message) === "login.user_terminated") {
                state.error = action.payload.payload.message;
            }
            state.fetching_next = false;
        })
            .addMatcher(loginApi.endpoints.fetchNewDevice.matchFulfilled, (state, action) => {
            state.this_device = action.payload.payload.new_device;
        })
            .addMatcher(loginApi.endpoints.fetchMfaAuth.matchFulfilled, (state, action) => {
            if (action.payload.payload.finished) {
                // Trigger fetching of /next on successful MFA authentication
                state.next_page = undefined;
            }
        })
            .addMatcher(loginApi.endpoints.fetchToU.matchFulfilled, (state, action) => {
            if (action.payload.payload.version) {
                state.tou.version = action.payload.payload.version;
            }
        });
    },
});
export default loginSlice;
